/**
 * SupportPanel — the 384px messenger panel shell (help.html `.widget`):
 * a dynamic top region (gradient Home header vs. sub-header with back/close),
 * a scrollable body, and a bottom Home/Messages/Help tab bar shown only on
 * those three top-level views.
 */

import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ArrowLeft, X, HelpCircle, Home, MessageSquare } from 'lucide-react';
import { media } from '../../../styles/media';
import { useSupportWidgetStore } from '../../../stores/supportWidgetStore';
import type { SupportWidgetTab } from '../../../stores/supportWidgetStore';

const Panel = styled.div`
  position: absolute;
  right: 0;
  bottom: calc(100% + ${({ theme }) => theme.spacing[3]});
  width: min(384px, calc(100vw - ${({ theme }) => theme.spacing[8]}));
  height: min(640px, 82vh);
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  overflow: hidden;

  ${media.tablet} {
    height: min(640px, 78vh);
  }

  &:focus {
    outline: none;
  }
`;

const GradientHeader = styled.div`
  flex-shrink: 0;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary.dark},
    ${({ theme }) => theme.colors.primary.base}
  );
  color: ${({ theme }) => theme.colors.text.white};
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[5]}
    ${({ theme }) => theme.spacing[6]};
  position: relative;
`;

const HeaderCloseButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing[3]};
  right: ${({ theme }) => theme.spacing[3]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.white};
  opacity: 0.85;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Greeting = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`;

const GreetingSub = styled.div`
  opacity: 0.85;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-top: 2px;
`;

const SubHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.primary.dark};
  color: ${({ theme }) => theme.colors.text.white};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const IconButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.white};
  opacity: 0.9;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TitleGroup = styled.div`
  flex: 1;
  min-width: 0;
`;

const SubHeaderTitle = styled.h3`
  margin: 0;
  /* GlobalStyles paints headings text.primary, which beats the color
     inherited from the dark SubHeader — restate white explicitly. */
  color: ${({ theme }) => theme.colors.text.white};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  /* Truncate rather than wrap unboundedly — the panel is a fixed 384px
     (ADO #542 items 9/10). */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubHeaderSubtitle = styled.div`
  margin-top: 1px;
  opacity: 0.8;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.backgrounds.page};
`;

const TabBar = styled.div`
  flex-shrink: 0;
  display: flex;
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.white};
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: ${({ theme }) => theme.spacing[2]} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary.base : theme.colors.text.light};

  svg {
    width: 18px;
    height: 18px;
  }
`;

export interface SupportPanelProps {
  userFirstName?: string;
  onClose: () => void;
  headerTitle?: string;
  /** Secondary line under headerTitle, e.g. "SR-1042" (ADO #542 items 9/10). */
  headerSubtitle?: string;
  onBack?: () => void;
  children: React.ReactNode;
}

const TAB_ITEMS: { key: SupportWidgetTab; label: string; icon: React.ReactNode }[] = [
  { key: 'home', label: 'Home', icon: <Home /> },
  { key: 'messages', label: 'Messages', icon: <MessageSquare /> },
  { key: 'help', label: 'Help', icon: <HelpCircle /> },
];

export function SupportPanel({
  userFirstName,
  onClose,
  headerTitle,
  headerSubtitle,
  onBack,
  children,
}: SupportPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const view = useSupportWidgetStore((s) => s.view);
  const setActiveTab = useSupportWidgetStore((s) => s.setActiveTab);

  const showTabBar = view === 'home' || view === 'messages' || view === 'help';
  const showGradientHeader = view === 'home';

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <Panel ref={panelRef} role="dialog" aria-label="Support" tabIndex={-1}>
      {showGradientHeader ? (
        <GradientHeader>
          <HeaderCloseButton type="button" onClick={onClose} aria-label="Close support">
            <X aria-hidden="true" />
          </HeaderCloseButton>
          <Greeting>Hi{userFirstName ? ` ${userFirstName}` : ''} 👋</Greeting>
          <GreetingSub>How can we help you today?</GreetingSub>
        </GradientHeader>
      ) : (
        <SubHeader>
          {onBack && (
            <IconButton type="button" onClick={onBack} aria-label="Back">
              <ArrowLeft aria-hidden="true" />
            </IconButton>
          )}
          <TitleGroup>
            <SubHeaderTitle>{headerTitle}</SubHeaderTitle>
            {headerSubtitle && <SubHeaderSubtitle>{headerSubtitle}</SubHeaderSubtitle>}
          </TitleGroup>
          <IconButton type="button" onClick={onClose} aria-label="Close support">
            <X aria-hidden="true" />
          </IconButton>
        </SubHeader>
      )}

      <Body>{children}</Body>

      {showTabBar && (
        <TabBar role="tablist" aria-label="Support">
          {TAB_ITEMS.map((tab) => (
            <Tab
              key={tab.key}
              type="button"
              role="tab"
              $active={view === tab.key}
              onClick={() => setActiveTab(tab.key)}
              aria-selected={view === tab.key}
            >
              {tab.icon}
              {tab.label}
            </Tab>
          ))}
        </TabBar>
      )}
    </Panel>
  );
}

export default SupportPanel;
